import { Sparkles } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import { CatalogOperationsAgentPanel } from '~/features/catalog-operations/components/catalog-operations-agent-panel';

/**
 * Shared layout for all Catalog Operations pages.
 *
 * The 3.0 release ships catalog operations WITHOUT the Lily Agent chat
 * sidebar (product decision). The implementation below stays in-tree — flip
 * `SHOW_AGENT_PANEL` to `true` to bring it back. Typed as `boolean` (not
 * `false`) so the unreachable branch keeps type-checking and the
 * `CatalogOperationsLayoutWithAgent` subcomponent is not flagged unused.
 *
 * TODO(3.1): restore the Lily Agent panel — set `SHOW_AGENT_PANEL = true`.
 *
 * Above `xl` (≥ 1280px) the panel is pinned to the left of the content.
 * Below `xl` it collapses to a floating toggle that opens a left-anchored
 * Sheet — both mounts share the same chat context via
 * `agentPanelContextKey.catalogOpsRoot` so the conversation stays in sync
 * as the viewport crosses the breakpoint.
 */
// `as boolean` widens the literal so TS does not narrow the `else` branch
// to unreachable — `CatalogOperationsLayoutWithAgent` must keep type-
// checking under both flag states.
const SHOW_AGENT_PANEL = false as boolean;

export function CatalogOperationsLayout({
  children,
  testId,
}: {
  children: ReactNode;
  /** Automation hook: `data-testid` for the page's outermost wrapper (e.g. `catalog-ops-page`). */
  testId?: string;
}) {
  if (!SHOW_AGENT_PANEL) {
    return (
      <div data-testid={testId} className="bg-gradient-page h-full overflow-y-auto p-6">
        {children}
      </div>
    );
  }
  return (
    <CatalogOperationsLayoutWithAgent {...(testId ? { testId } : {})}>
      {children}
    </CatalogOperationsLayoutWithAgent>
  );
}

/** Original layout (desktop pinned panel + mobile Sheet toggle). Kept as a
 *  separate component so its `useState` is not paid when the panel is
 *  hidden. Restore by flipping `SHOW_AGENT_PANEL` above. */
function CatalogOperationsLayoutWithAgent({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}) {
  const [agentOpen, setAgentOpen] = useState(false);

  return (
    <div data-testid={testId} className="flex h-full min-h-0">
      {/* Pinned desktop panel — xl and up. */}
      <div className="hidden xl:flex">
        <CatalogOperationsAgentPanel />
      </div>

      <div className="bg-gradient-page relative h-full flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-6">{children}</div>

        {/* Floating toggle — visible below xl only. */}
        <button
          type="button"
          aria-label="Open Lily Agent"
          data-testid="catalog-ops-agent-toggle-button"
          onClick={() => {
            setAgentOpen(true);
          }}
          className="bg-primary hover:bg-primary/90 fixed right-6 bottom-6 z-40 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full text-white shadow-lg transition-colors xl:hidden"
        >
          <Sparkles className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {/* Sheet — only mounted/used below xl via the toggle. The override
          classes force the shell's `hidden md:flex` to render at every
          breakpoint and let the Sheet own the width + border. */}
      <Sheet open={agentOpen} onOpenChange={setAgentOpen}>
        <SheetContent
          data-testid="catalog-ops-agent-sheet"
          side="left"
          className="flex w-full max-w-[520px] flex-col gap-0 p-0 sm:max-w-[520px]"
        >
          <SheetTitle className="sr-only">Lily Agent</SheetTitle>
          <CatalogOperationsAgentPanel className="!flex h-full !w-full !border-r-0" />
        </SheetContent>
      </Sheet>
    </div>
  );
}
